import { useCallback, useEffect, useState } from 'react';
import { fetchOrder, fetchUnshippedOrders } from '../../api/orders';
import { getUrgentUnshippedOrders, getOrderProductLabel } from './shipByUrgency';
import { useNow } from './useNow';
import { getPriorityOrderIds, subscribePriorityOrderIds } from '../customer-service/priorityOrdersStorage';
import { subscribeShippingAlertsRefresh } from './shippingAlertsRefresh';

export function useShippingAlerts(token) {
  const now = useNow();
  const [orders, setOrders] = useState([]);
  const [priorityOrders, setPriorityOrders] = useState([]);
  const [loading, setLoading] = useState(Boolean(token));

  const refresh = useCallback(async () => {
    if (!token) {
      setOrders([]);
      setPriorityOrders([]);
      setLoading(false);
      return;
    }

    try {
      const payload = await fetchUnshippedOrders(token);
      setOrders(payload.orders || []);

      const pIds = getPriorityOrderIds();
      const pOrders = await Promise.all(
        pIds.map(async (id) => {
          try {
            const result = await fetchOrder(token, id);
            return result.order || null;
          } catch {
            return null;
          }
        })
      );
      setPriorityOrders(pOrders.filter(Boolean));
    } catch {
      setOrders([]);
      setPriorityOrders([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();

    const unsubscribePriority = subscribePriorityOrderIds(() => {
      refresh();
    });

    const unsubscribeSync = subscribeShippingAlertsRefresh(() => {
      refresh();
    });

    const timer = window.setInterval(refresh, 5 * 60 * 1000);

    return () => {
      unsubscribePriority();
      unsubscribeSync();
      window.clearInterval(timer);
    };
  }, [refresh]);

  const alerts = [
    ...getUrgentUnshippedOrders(orders, now),
    ...priorityOrders.map((order) => ({
      type: 'priority',
      orderId: order.id,
      label: getOrderProductLabel(order),
      amazonOrderId: order.amazonOrderId,
    })),
  ];

  return { alerts, loading, refresh };
}
